import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';

const CounterStat = ({ value, suffix, label, delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 1400;
    const startTime = performance.now();
    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * value));
      if (progress < 1) requestAnimationFrame(animate);
      else setCount(value);
    };
    const t = setTimeout(() => requestAnimationFrame(animate), delay);
    return () => clearTimeout(t);
  }, [inView, value, delay]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: delay / 1000 }}
      className="text-center"
    >
      <div className="font-display text-[40px] sm:text-[56px] md:text-[72px] font-semibold text-neutral-900 leading-none tracking-tight">
        {count}{suffix}
      </div>
      <div className="mt-3 text-[12px] sm:text-[13px] md:text-[14px] text-neutral-500 leading-tight px-1">{label}</div>
    </motion.div>
  );
};

const Stats = () => {
  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-3 gap-4 sm:gap-6 md:gap-10">
          <CounterStat value={4} suffix="+" label="Years of teaching" delay={0} />
          <CounterStat value={300} suffix="+" label="Students taught" delay={150} />
          <CounterStat value={100} suffix="%" label="Satisfaction" delay={300} />
        </div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="mt-14 text-center text-[14px] md:text-[15px] text-neutral-600 max-w-2xl mx-auto leading-relaxed"
        >
          More than statistics — these represent students, progress, and trust in action.
          Behind every figure is a story of learning, improvement, and growing confidence.
        </motion.p>
      </div>
    </section>
  );
};

export default Stats;
